import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  useTheme,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Stack
} from '@mui/material';
import Header from '../components/Header';
import {
  Check as CheckIcon,
  QrCode as QrCodeIcon,
  Star as StarIcon,
  Info as InfoIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Support as SupportIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config';
import { Package, PaymentInfo, SubscriptionPeriod } from '../types';

const Payment: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState<{ standard: Package; gold: Package } | null>(null);
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<SubscriptionPeriod>({
    months: 1,
    displayName: '1 tháng',
    price: '150.000 VNĐ'
  });

  // Mock functions for Header props
  const handleExport = () => {
    // This function is not used in Payment page
  };

  const isNormalizing = false; // Payment page doesn't have normalization

  useEffect(() => {
    fetchPackages();
    fetchPaymentInfo();
  }, []);

  // Handle navigation when user is not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.PAYMENT.PACKAGES);
      setPackages(response.data);
    } catch (error) {
      console.error('Error fetching packages:', error);
      setError('Không thể tải thông tin gói dịch vụ');
    }
  };

  const subscriptionPeriods: SubscriptionPeriod[] = [
    {
      months: 1,
      displayName: '1 tháng',
      price: '150.000 VNĐ'
    },
    {
      months: 3,
      displayName: '3 tháng',
      price: '450.000 VNĐ',
      savings: 'Tiết kiệm 0 VNĐ'
    },
    {
      months: 6,
      displayName: '6 tháng',
      price: '900.000 VNĐ',
      savings: 'Tiết kiệm 0 VNĐ'
    }
  ];

  const fetchPaymentInfo = async (months: number = 1) => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.PAYMENT.QR_CODE}?months=${months}`);
      setPaymentInfo(response.data);
    } catch (error) {
      console.error('Error fetching payment info:', error);
    }
  };

  const handleUpgrade = async (packageType: string) => {
    if (packageType === 'GOLD') {
      await fetchPaymentInfo(selectedPeriod.months);
      setShowQRDialog(true);
    }
  };

  const handlePeriodChange = (period: SubscriptionPeriod) => {
    setSelectedPeriod(period);
  };

  const handleCloseQRDialog = () => {
    setShowQRDialog(false);
    setCopiedField(null);
  };



  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Failed to copy text: ', error);
    }
  };

  if (!packages) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Đang tải...
        </Typography>
      </Container>
    );
  }

  return (
    <>
      <Header onExport={handleExport} isNormalizing={isNormalizing} />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Nâng cấp gói dịch vụ
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Chọn gói phù hợp với nhu cầu sử dụng của bạn
        </Typography>
        
        {/* Current Package Info */}
        {user && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <Typography variant="h6" gutterBottom>
              Gói hiện tại: {user.packageType === 'GOLD' ? 'Premium' : 'Cơ bản'}
            </Typography>
            <Typography variant="body2">
              Coin xanh: {user.greenCoins} 🟢 | Coin vàng: {user.goldCoins} 🟡
            </Typography>
          </Paper>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Standard Package */}
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              border: user?.packageType === 'STANDARD' ? `2px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0',
              position: 'relative',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8]
              }
            }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  {packages.standard.displayName}
                </Typography>
                <Typography variant="h4" color="primary" gutterBottom>
                  {packages.standard.price}
                </Typography>
                {user?.packageType === 'STANDARD' && (
                  <Chip 
                    label="Gói hiện tại" 
                    color="primary" 
                    variant="outlined"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Coin mỗi ngày:
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Chip 
                    label={`${packages.standard.greenCoinsPerDay} 🟢`} 
                    size="medium" 
                    color="success" 
                    variant="outlined"
                  />
                  <Chip 
                    label={`${packages.standard.goldCoinsPerDay} 🟡`} 
                    size="medium" 
                    color="warning" 
                    variant="outlined"
                  />
                </Stack>
              </Box>

              <List dense>
                {packages.standard.features.map((feature, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
            <CardActions>
              <Button 
                fullWidth 
                variant="outlined" 
                disabled={user?.packageType === 'STANDARD'}
                sx={{ py: 1.5 }}
              >
                {user?.packageType === 'STANDARD' ? 'Đang sử dụng' : 'Chọn gói'}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Gold Package */}
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              border: user?.packageType === 'GOLD' ? `2px solid ${theme.palette.warning.main}` : '1px solid #e0e0e0',
              position: 'relative',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8]
              }
            }}
          >
            {user?.packageType !== 'GOLD' && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: 16,
                  zIndex: 1
                }}
              >
                <Chip
                  icon={<StarIcon />}
                  label="Khuyến nghị"
                  color="warning"
                  size="small"
                />
              </Box>
            )}
            
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  {packages.gold.displayName}
                </Typography>
                
                {user?.packageType !== 'GOLD' && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Chọn thời gian đăng ký:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {subscriptionPeriods.map((period) => (
                        <Chip
                          key={period.months}
                          label={period.displayName}
                          variant={selectedPeriod.months === period.months ? "filled" : "outlined"}
                          color={selectedPeriod.months === period.months ? "warning" : "default"}
                          onClick={() => handlePeriodChange(period)}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'warning.light',
                              color: 'white'
                            }
                          }}
                        />
                      ))}
                    </Box>
                    <Typography variant="h6" color="warning.main" sx={{ mt: 1 }}>
                      {selectedPeriod.price}
                    </Typography>
                    {selectedPeriod.savings && (
                      <Typography variant="caption" color="success.main">
                        {selectedPeriod.savings}
                      </Typography>
                    )}
                  </Box>
                )}
                
                {user?.packageType === 'GOLD' && (
                  <Typography variant="h4" color="warning.main" gutterBottom>
                    {packages.gold.price}
                  </Typography>
                )}
                
                {user?.packageType === 'GOLD' && (
                  <Chip 
                    label="Gói hiện tại" 
                    color="warning" 
                    variant="outlined"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Coin mỗi tháng:
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Chip 
                    label={`${packages.gold.greenCoinsPerDay * 30} 🟢`} 
                    size="medium" 
                    color="success" 
                    variant="outlined"
                  />
                  <Chip 
                    label={`${packages.gold.goldCoinsPerDay * 30} 🟡`} 
                    size="medium" 
                    color="warning" 
                    variant="outlined"
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  (Tương đương {packages.gold.greenCoinsPerDay} 🟢 và {packages.gold.goldCoinsPerDay} 🟡 mỗi ngày)
                </Typography>
              </Box>

              <List dense>
                {packages.gold.features.map((feature, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Lưu ý:</strong> Gói Premium có tính năng reset coin theo chu kỳ tháng. Coin xanh được reset về 4.500 mỗi tháng, coin vàng được cộng dồn 450 mỗi tháng.
                </Typography>
              </Alert>
            </CardContent>
            <CardActions>
              <Button 
                fullWidth 
                variant="contained" 
                color="warning"
                startIcon={<StarIcon />}
                onClick={() => handleUpgrade('GOLD')}
                disabled={user?.packageType === 'GOLD'}
                sx={{ py: 1.5 }}
              >
                {user?.packageType === 'GOLD' ? 'Đang sử dụng' : 'Nâng cấp ngay'}
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      {/* Features Comparison */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
          So sánh tính năng
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <SpeedIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Tốc độ xử lý
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Premium được ưu tiên xử lý với tốc độ nhanh hơn
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <SecurityIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Bảo mật cao
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Dữ liệu được mã hóa và bảo vệ an toàn
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <SupportIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Hỗ trợ 24/7
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Đội ngũ hỗ trợ chuyên nghiệp sẵn sàng giúp đỡ
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <StarIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Reset hàng tháng
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Coin xanh reset về 4.500, coin vàng cộng dồn 450 mỗi tháng
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* QR Code Dialog */}
      <Dialog 
        open={showQRDialog} 
        onClose={handleCloseQRDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QrCodeIcon />
              Thanh toán nâng cấp gói Premium - {selectedPeriod.displayName}
            </Box>
            <IconButton onClick={handleCloseQRDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {paymentInfo && (
            <Box sx={{ textAlign: 'center' }}>
              <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
                <img 
                  src={paymentInfo.qrCodeUrl} 
                  alt="QR Code" 
                  style={{ maxWidth: '250px', height: 'auto' }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Quét mã QR để chuyển khoản
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  QR Code được tạo tự động từ sepay.vn với nội dung là email của bạn
                </Typography>
              </Paper>
              
              <Typography variant="h6" gutterBottom>
                Thông tin chuyển khoản
              </Typography>
              
              <Paper sx={{ p: 2, mb: 2 }}>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">
                      <strong>Ngân hàng:</strong>
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {paymentInfo.bankInfo.bankName}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">
                      <strong>Số tài khoản:</strong>
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {paymentInfo.bankInfo.accountNumber}
                      </Typography>
                      <Tooltip title={copiedField === 'account' ? 'Đã sao chép!' : 'Sao chép'}>
                        <IconButton 
                          size="small" 
                          onClick={() => copyToClipboard(paymentInfo.bankInfo.accountNumber, 'account')}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">
                      <strong>Tên tài khoản:</strong>
                    </Typography>
                    <Typography variant="body2">
                      {paymentInfo.bankInfo.accountName}
                    </Typography>
                  </Box>
                  
                                                       <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">
                      <strong>Thời gian đăng ký:</strong>
                    </Typography>
                    <Typography variant="body2">
                      {selectedPeriod.displayName}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">
                      <strong>Số tiền:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                      {paymentInfo.bankInfo.amount} {paymentInfo.bankInfo.currency}
                    </Typography>
                  </Box>
                   
                   <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                     <Typography variant="body2">
                       <strong>Nội dung:</strong>
                     </Typography>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                       <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                         {paymentInfo.bankInfo.transferContent}
                       </Typography>
                       <Tooltip title={copiedField === 'content' ? 'Đã sao chép!' : 'Sao chép'}>
                         <IconButton 
                           size="small" 
                           onClick={() => copyToClipboard(paymentInfo.bankInfo.transferContent, 'content')}
                         >
                           <CopyIcon fontSize="small" />
                         </IconButton>
                       </Tooltip>
                     </Box>
                   </Box>
                   
                   <Alert severity="info" sx={{ mt: 1 }}>
                     <Typography variant="body2">
                       <strong>Lưu ý:</strong> Nội dung chuyển khoản là email của bạn để dễ dàng xác định giao dịch.
                     </Typography>
                   </Alert>
                </Stack>
              </Paper>

              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Thanh toán tự động:</strong> Sau khi chuyển khoản thành công, hệ thống sẽ tự động nâng cấp gói của bạn trong vòng 1-2 phút.
                </Typography>
              </Alert>

              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Lưu ý:</strong> Vui lòng giữ lại biên lai chuyển khoản để đối soát khi cần thiết. Nội dung chuyển khoản sẽ là email của bạn.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseQRDialog}>
            Đóng
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </>
  );
};

export default Payment; 